import type { TagCategoriesConfig } from '@racemap/sdk/schema/events';
import type { Immutable } from 'immer';

/**
 * Assembles a merged tag group configuration by combining the existing config with
 * the current categories of starters and the user's selected tag keys.
 *
 * - Visibility is determined as follows:
 *   - If no tag keys are selected, all keys are set to visible.
 *   - If a key exists in the current config but is no longer in the starters' categories,
 *     its existing visibility state is preserved.
 *   - Otherwise, visibility is set based on whether the key is included in `selectedTagKeys`.
 * - Index assignment preserves existing indexes and assigns new non-colliding indexes to new keys.
 * - Non-existing keys inherit default values for `default`, `groupStarterBy`, and `index`.
 *
 * @param tagCategoriesConfig - The current tag categories configuration.
 * @param categoriesOfStarters - The list of tag category keys derived from starters.
 * @param selectedTagKeys - The list of tag keys currently selected/visible by the user.
 * @returns A new tag categories configuration object merging all keys from both sources.
 */
export function assembleTagGroupConfiguration(
  tagCategoriesConfig: Immutable<TagCategoriesConfig>,
  categoriesOfStarters: Array<string>,
  selectedTagKeys: Array<string>,
) {
  const keys = new Set([...Object.keys(tagCategoriesConfig), ...categoriesOfStarters]);
  // We need to keep track of used indexes to avoid collisions when assigning indexes to new keys
  const usedIndexes = new Set(Object.values(tagCategoriesConfig).map((config) => config.index));

  const nextConfig = Object.fromEntries(
    Array.from(keys).map((key, index) => {
      const existing = tagCategoriesConfig[key];

      let visible: boolean;
      if (selectedTagKeys.length === 0) {
        // If no keys are selected, we set all keys to visible
        visible = true;
      } else if (existing && !categoriesOfStarters.includes(key)) {
        // If a key exists in the current config but is not in the new tag groups, we want to keep its visibility state
        visible = existing.visible;
      } else {
        // For new keys or keys that are still in the tag groups, we set visibility based on whether they are selected
        visible = selectedTagKeys.includes(key);
      }

      let finalIndex: number;
      if (existing) {
        finalIndex = existing.index;
      } else if (usedIndexes.has(index)) {
        // If the default index is already used, we assign the next available index
        finalIndex = Math.max(...usedIndexes) + 1;
        usedIndexes.add(finalIndex);
      } else {
        // If the default index is not used, we can use it
        finalIndex = index;
        usedIndexes.add(finalIndex);
      }

      return [
        key,
        {
          visible,
          default: existing?.default ?? [],
          groupStarterBy: existing?.groupStarterBy ?? false,
          index: finalIndex,
        },
      ];
    }),
  );
  return nextConfig;
}
